import { useState } from "react";
import { useLanguage } from "../LanguageContext";
import { useTheme } from "../ThemeContext";
import { 
  ArrowRight, 
  ChevronLeft,
  Sparkles,
  Crown,
  Star,
  Gem
} from "lucide-react";
import { Link } from "react-router-dom";

const TokenDesignPage = () => {
  const { language } = useLanguage();
  const { palette } = useTheme();
  const [selectedToken, setSelectedToken] = useState(0);

  const tokenDesigns = [
    {
      id: 0,
      name: language === 'fr' ? "Pièce de Huit Moderne" : "Modern Piece of Eight",
      description: language === 'fr' 
        ? "Inspiration historique du peso espagnol avec design crypto moderne"
        : "Historical Spanish peso inspiration with modern crypto design",
      style: "classic"
    },
    {
      id: 1,
      name: language === 'fr' ? "Fleur de Lys Québécoise" : "Quebec Fleur de Lys",
      description: language === 'fr'
        ? "Héritage Nouvelle-France avec la fleur de lys emblématique"
        : "New France heritage with the iconic fleur de lys",
      style: "quebec"
    },
    {
      id: 2,
      name: language === 'fr' ? "Crypto Luxe Premium" : "Premium Crypto Luxe",
      description: language === 'fr'
        ? "Design minimaliste moderne avec finition holographique"
        : "Modern minimalist design with holographic finish",
      style: "luxe"
    },
    {
      id: 3,
      name: language === 'fr' ? "Édition Papineau 1837" : "Papineau 1837 Edition",
      description: language === 'fr'
        ? "Édition commémorative honorant le chef des Patriotes"
        : "Commemorative edition honoring the Patriote leader",
      style: "papineau"
    }
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-b ${palette.bg} pt-8 pb-20`}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Piastre.one</span>
          </Link>
          
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            {language === 'fr' ? "Token PIASTRE" : "PIASTRE Token"}
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            {language === 'fr' 
              ? "Explorez les concepts de design pour notre token en or sur la blockchain Stellar"
              : "Explore design concepts for our gold token on the Stellar blockchain"
            }
          </p>
        </div>

        {/* Main Token Display */}
        <div className="flex flex-col lg:flex-row gap-12 items-center justify-center mb-16">
          {/* Large Token Preview */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 rounded-full blur-3xl scale-150" style={{ backgroundColor: `${palette.primary}20` }}></div>
            
            {/* Token */}
            <div className="relative">
              {selectedToken === 0 && <TokenClassic />}
              {selectedToken === 1 && <TokenQuebec />}
              {selectedToken === 2 && <TokenLuxe />}
              {selectedToken === 3 && <TokenPapineau />}
            </div>
          </div>

          {/* Token Info */}
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${palette.primary}20`, color: palette.primary }}>
              <Sparkles className="w-4 h-4" />
              {language === 'fr' ? "Concept de Design" : "Design Concept"}
            </div>
            <h2 className="font-outfit text-3xl font-semibold text-white mb-4">
              {tokenDesigns[selectedToken].name}
            </h2>
            <p className="text-slate-400 text-lg mb-6">
              {tokenDesigns[selectedToken].description}
            </p>
            
            {/* Token Specs */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <h4 className="text-white font-semibold mb-4">
                {language === 'fr' ? "Spécifications" : "Specifications"}
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Blockchain</span>
                  <span className="text-white">Stellar (XLM)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Symbol</span>
                  <span className="text-white">PIASTRE</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{language === 'fr' ? "Couleur" : "Color"}</span>
                  <span className="text-amber-400">Or / Gold</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{language === 'fr' ? "Style" : "Style"}</span>
                  <span className="text-white">{tokenDesigns[selectedToken].name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Token Selection Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {tokenDesigns.map((token, index) => (
            <button
              key={token.id}
              onClick={() => setSelectedToken(index)}
              className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                selectedToken === index 
                  ? 'bg-white/10' 
                  : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
              }`}
              style={selectedToken === index ? { borderColor: palette.primary, backgroundColor: `${palette.primary}15` } : {}}
            >
              <div className="w-20 h-20 mx-auto mb-3">
                {index === 0 && <TokenClassicMini />}
                {index === 1 && <TokenQuebecMini />}
                {index === 2 && <TokenLuxeMini />}
                {index === 3 && <TokenPapineauMini />}
              </div>
              <p className="text-sm font-medium" style={{ color: selectedToken === index ? palette.primary : '#94a3b8' }}>
                {token.name}
              </p>
            </button>
          ))}
        </div>

        {/* All Tokens Showcase */}
        <div className="text-center mb-12">
          <h3 className="font-outfit text-2xl font-semibold text-white mb-8">
            {language === 'fr' ? "Tous les Concepts" : "All Concepts"}
          </h3>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <TokenClassic size="medium" />
              <p className="text-slate-400 mt-4 text-sm">Pièce de Huit</p>
            </div>
            <div className="text-center">
              <TokenQuebec size="medium" />
              <p className="text-slate-400 mt-4 text-sm">Fleur de Lys</p>
            </div>
            <div className="text-center">
              <TokenLuxe size="medium" />
              <p className="text-slate-400 mt-4 text-sm">Crypto Luxe</p>
            </div>
            <div className="text-center">
              <TokenPapineau size="medium" />
              <p className="text-slate-400 mt-4 text-sm">Édition Papineau</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 px-8 py-4 rounded-full font-semibold text-lg hover:from-amber-400 hover:to-amber-500 transition-all"
          >
            {language === 'fr' ? "Découvrir Piastre.one" : "Discover Piastre.one"}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// Token Design 1: Classic Piece of Eight
const TokenClassic = ({ size = "large" }) => {
  const dimensions = size === "large" ? "w-72 h-72" : size === "medium" ? "w-40 h-40" : "w-20 h-20";
  const fontSize = size === "large" ? "text-6xl" : size === "medium" ? "text-3xl" : "text-lg";
  const borderText = size === "large" ? "text-xs" : "text-[6px]";
  
  return (
    <div className={`${dimensions} relative`} style={{ perspective: '1000px' }}>
      <div 
        className="w-full h-full rounded-full relative animate-spin-slow"
        style={{
          background: 'linear-gradient(145deg, #ffd700, #b8860b, #ffd700, #daa520)',
          boxShadow: `
            0 0 60px rgba(255, 215, 0, 0.4),
            inset 0 2px 10px rgba(255, 255, 255, 0.5),
            inset 0 -2px 10px rgba(0, 0, 0, 0.3),
            0 10px 30px rgba(0, 0, 0, 0.5)
          `,
          animation: 'float 3s ease-in-out infinite'
        }}
      >
        {/* Outer Ring - Ridged Edge */}
        <div 
          className="absolute inset-2 rounded-full"
          style={{
            background: 'linear-gradient(145deg, #ffd700, #b8860b)',
            boxShadow: 'inset 0 2px 5px rgba(255,255,255,0.4), inset 0 -2px 5px rgba(0,0,0,0.2)'
          }}
        >
          {/* Inner Circle */}
          <div 
            className="absolute inset-3 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, #daa520, #b8860b, #ffd700)',
              boxShadow: 'inset 0 4px 10px rgba(0,0,0,0.2)'
            }}
          >
            {/* Center Symbol */}
            <div className="text-center">
              <span 
                className={`${fontSize} font-serif font-bold`}
                style={{ 
                  color: '#8B4513',
                  textShadow: '1px 1px 2px rgba(255,215,0,0.5), -1px -1px 1px rgba(0,0,0,0.3)'
                }}
              >
                ₱
              </span>
            </div>
          </div>
        </div>
        
        {/* Circular Text */}
        {size === "large" && (
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
            <defs>
              <path id="textCircle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"/>
            </defs>
            <text className="fill-amber-900" style={{ fontSize: '5px', fontWeight: 'bold' }}>
              <textPath href="#textCircle">
                • PIASTRE • ONE • STELLAR • QUÉBEC •
              </textPath>
            </text>
          </svg>
        )}
      </div>
    </div>
  );
};

const TokenClassicMini = () => <TokenClassic size="mini" />;

// Token Design 2: Quebec Fleur de Lys
const TokenQuebec = ({ size = "large" }) => {
  const dimensions = size === "large" ? "w-72 h-72" : size === "medium" ? "w-40 h-40" : "w-20 h-20";
  
  return (
    <div className={`${dimensions} relative`}>
      <div 
        className="w-full h-full rounded-full relative"
        style={{
          background: 'linear-gradient(145deg, #ffd700, #c9a227, #ffd700)',
          boxShadow: `
            0 0 60px rgba(255, 215, 0, 0.4),
            inset 0 4px 15px rgba(255, 255, 255, 0.5),
            inset 0 -4px 15px rgba(0, 0, 0, 0.3),
            0 15px 40px rgba(0, 0, 0, 0.5)
          `,
          animation: 'float 3s ease-in-out infinite'
        }}
      >
        {/* Blue Inner Circle (Quebec colors) */}
        <div 
          className="absolute inset-4 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(145deg, #1e3a5f, #0d2137)',
            boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.5), 0 2px 5px rgba(255,215,0,0.3)'
          }}
        >
          {/* Fleur de Lys SVG */}
          <svg 
            viewBox="0 0 100 100" 
            className={size === "large" ? "w-32 h-32" : size === "medium" ? "w-20 h-20" : "w-10 h-10"}
          >
            <defs>
              <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffd700" />
                <stop offset="50%" stopColor="#ffec8b" />
                <stop offset="100%" stopColor="#daa520" />
              </linearGradient>
            </defs>
            {/* Simplified Fleur de Lys */}
            <path 
              d="M50 10 C55 20, 65 25, 70 35 C75 45, 70 55, 60 55 L60 70 L75 70 L75 75 L60 75 L60 90 L40 90 L40 75 L25 75 L25 70 L40 70 L40 55 C30 55, 25 45, 30 35 C35 25, 45 20, 50 10 Z"
              fill="url(#goldGradient)"
              style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }}
            />
            {/* Center detail */}
            <circle cx="50" cy="45" r="5" fill="#1e3a5f" />
          </svg>
        </div>
        
        {/* Gold Border Ring */}
        <div 
          className="absolute inset-2 rounded-full border-4 border-amber-300/30"
          style={{ boxShadow: 'inset 0 0 10px rgba(255,215,0,0.3)' }}
        />
      </div>
    </div>
  );
};

const TokenQuebecMini = () => <TokenQuebec size="mini" />;

// Token Design 3: Crypto Luxe Premium
const TokenLuxe = ({ size = "large" }) => {
  const dimensions = size === "large" ? "w-72 h-72" : size === "medium" ? "w-40 h-40" : "w-20 h-20";
  const fontSize = size === "large" ? "text-8xl" : size === "medium" ? "text-5xl" : "text-2xl";
  
  return (
    <div className={`${dimensions} relative`}>
      {/* Holographic Ring Effect */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: 'conic-gradient(from 0deg, #ffd700, #ff69b4, #00ffff, #ffd700, #ff69b4, #00ffff, #ffd700)',
          animation: 'spin 4s linear infinite',
          opacity: 0.6,
          filter: 'blur(8px)'
        }}
      />
      
      <div 
        className="absolute inset-1 rounded-full"
        style={{
          background: 'linear-gradient(145deg, #1a1a2e, #16213e, #1a1a2e)',
          boxShadow: `
            0 0 80px rgba(255, 215, 0, 0.3),
            inset 0 2px 20px rgba(255, 255, 255, 0.1),
            0 20px 50px rgba(0, 0, 0, 0.6)
          `,
          animation: 'float 3s ease-in-out infinite'
        }}
      >
        {/* Inner Glow Ring */}
        <div 
          className="absolute inset-4 rounded-full"
          style={{
            background: 'linear-gradient(145deg, transparent, rgba(255,215,0,0.1), transparent)',
            border: '1px solid rgba(255,215,0,0.3)'
          }}
        />
        
        {/* Center Content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span 
            className={`${fontSize} font-outfit font-bold`}
            style={{ 
              background: 'linear-gradient(145deg, #ffd700, #ffec8b, #ffd700)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 10px rgba(255,215,0,0.5))'
            }}
          >
            P
          </span>
        </div>
        
        {/* Stellar Stars */}
        {size === "large" && (
          <>
            <Sparkles className="absolute top-8 right-12 w-4 h-4 text-amber-400/60" />
            <Sparkles className="absolute bottom-12 left-8 w-3 h-3 text-amber-400/40" />
            <Star className="absolute top-16 left-12 w-3 h-3 text-cyan-400/40" />
          </>
        )}
      </div>
    </div>
  );
};

const TokenLuxeMini = () => <TokenLuxe size="mini" />;

// Token Design 4: Papineau Edition
const TokenPapineau = ({ size = "large" }) => {
  const dimensions = size === "large" ? "w-72 h-72" : size === "medium" ? "w-40 h-40" : "w-20 h-20";
  
  return (
    <div className={`${dimensions} relative`}>
      <div 
        className="w-full h-full rounded-full relative"
        style={{
          background: 'linear-gradient(145deg, #cd7f32, #b87333, #daa520, #cd7f32)',
          boxShadow: `
            0 0 60px rgba(205, 127, 50, 0.4),
            inset 0 4px 15px rgba(255, 255, 255, 0.4),
            inset 0 -4px 15px rgba(0, 0, 0, 0.4),
            0 15px 40px rgba(0, 0, 0, 0.5)
          `,
          animation: 'float 3s ease-in-out infinite'
        }}
      >
        {/* Antique Inner */}
        <div 
          className="absolute inset-3 rounded-full flex flex-col items-center justify-center"
          style={{
            background: 'linear-gradient(145deg, #8b7355, #6b5344, #8b7355)',
            boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.4)'
          }}
        >
          {/* Portrait Placeholder */}
          <div 
            className={`${size === "large" ? "w-24 h-28" : size === "medium" ? "w-14 h-16" : "w-8 h-9"} rounded-full mb-2`}
            style={{
              background: 'linear-gradient(145deg, #cd7f32, #daa520)',
              boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.3)'
            }}
          >
            {/* Silhouette */}
            <svg viewBox="0 0 50 60" className="w-full h-full p-2">
              <ellipse cx="25" cy="20" rx="12" ry="14" fill="#5c4a3a" />
              <path d="M10 55 Q15 35, 25 35 Q35 35, 40 55 Z" fill="#5c4a3a" />
            </svg>
          </div>
          
          {/* Year */}
          {size !== "mini" && (
            <span 
              className={`font-serif font-bold ${size === "large" ? "text-lg" : "text-xs"}`}
              style={{ color: '#daa520', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
            >
              1837
            </span>
          )}
        </div>
        
        {/* Decorative Border */}
        <div 
          className="absolute inset-1 rounded-full"
          style={{
            border: '3px solid rgba(218,165,32,0.4)',
            borderStyle: 'dashed'
          }}
        />
      </div>
    </div>
  );
};

const TokenPapineauMini = () => <TokenPapineau size="mini" />;

export default TokenDesignPage;
